<template>
  <div class="main-panel">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
    </div>

    <header class="header">
      <div>
        <h1>Revisar reportes de fallas</h1>
        <p class="sub">Consulta reportes y agrega comentarios de seguimiento.</p>
      </div>
      <div class="header-actions">
        <button class="btn btn--ghost" @click="loadReports">Actualizar</button>
        <button class="btn btn--ghost" @click="goBack">Regresar</button>
      </div>
    </header>

    <section class="card">
      <p v-if="error" class="error">{{ error }}</p>

      <div v-if="loading" class="loading">Cargando reportes...</div>

      <div v-else-if="reports.length" class="report-list">
        <article v-for="report in reports" :key="report.id" class="report-item">
          <div class="top-row">
            <strong>#{{ report.id }} · {{ report.unit_short_id || '-' }}</strong>
            <span>{{ formatDate(report.request_time) }}</span>
          </div>
          <p class="meta"><b>Reporta:</b> {{ report.reporter_name || '-' }}</p>
          <p class="meta"><b>Observacion:</b> {{ formatDate(report.observation_tiem) }}</p>
          <p class="meta"><b>Sistema:</b> {{ report.system }}</p>
          <p class="description">{{ report.description }}</p>

          <label class="field" v-if="canComment">
            <span>Comentarios</span>
            <textarea v-model="report.commentsDraft" rows="3"></textarea>
          </label>
          <p v-else class="meta"><b>Comentarios:</b> {{ report.comments || 'Sin comentarios' }}</p>

          <div class="actions" v-if="canComment">
            <button
              v-if="canScheduleMaintenance"
              class="btn btn--ghost"
              :disabled="Boolean(report.saving)"
              @click="openScheduleModal(report)"
            >
              Programar mantenimiento
            </button>
            <button class="btn btn--primary" :disabled="Boolean(report.saving)" @click="saveComments(report)">
              {{ report.saving ? 'Guardando...' : 'Guardar comentario' }}
            </button>
          </div>
        </article>
      </div>

      <div v-else class="empty">No hay reportes de fallas registrados.</div>
    </section>

    <div v-if="showScheduleModal" class="modal-overlay" @click.self="closeScheduleModal">
      <div class="modal-card schedule-modal">
        <div class="modal-head">
          <h3>Programar mantenimiento correctivo</h3>
          <button class="close-btn" @click="closeScheduleModal">Cerrar</button>
        </div>
        <div class="modal-body">
          <div class="form-row">
            <label>Unidad</label>
            <input type="text" :value="scheduleForm.unit_short_id" disabled />
          </div>
          <div class="form-row">
            <label>Motivo de servicio</label>
            <input type="text" v-model="scheduleForm.motive_of_service" disabled />
          </div>
          <div class="form-row">
            <label>Programada para</label>
            <input type="datetime-local" v-model="scheduleForm.scheduled_for" />
          </div>
          <div class="form-row">
            <label>Descripcion de servicio</label>
            <textarea v-model="scheduleForm.service_description" />
          </div>
        </div>
        <div class="modal-actions footer-actions">
          <button class="small-btn" @click="closeScheduleModal">Cancelar</button>
          <button class="small-btn primary" :disabled="creatingOrder" @click="submitScheduledOrder">
            {{ creatingOrder ? 'Guardando...' : 'Crear orden' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from 'sweetalert2'

export default {
  name: 'ReviewUnitMalfunctionReports',
  data() {
    return {
      permissions: [],
      reports: [],
      loading: false,
      error: '',
      creatingOrder: false,
      showScheduleModal: false,
      selectedReportForSchedule: null,
      scheduleForm: {
        unit_short_id: '',
        motive_of_service: 'Correctivo',
        scheduled_for: '',
        service_description: 'ORDEN CREADA AUTOMATICAMENTE',
      },
    }
  },
  computed: {
    canCreate() {
      return this.hasPerm('maintenance.create_incident')
    },
    canReview() {
      return this.hasPerm('maintenance.view_incident_table')
    },
    canComment() {
      return this.canCreate || this.canReview
    },
    canScheduleMaintenance() {
      return this.hasPerm('maintenance.create_order')
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.permissions = data.permissions || []
        if (!this.canCreate && !this.canReview) {
          this.$router.push({ path: '/maintenance/malfunction-reports' })
          return
        }
        this.loadReports()
      } catch (error) {
        this.$router.push({ path: '/maintenance/malfunction-reports' })
      }
    },
    async loadReports() {
      const apiDirectory = process.env.API_DIRECTORY
      this.error = ''
      this.loading = true
      try {
        const { data } = await axios.get(`${apiDirectory}/unit-malfunction-reports`, {
          headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
        })
        this.reports = (Array.isArray(data) ? data : []).map((item) => ({
          ...item,
          commentsDraft: item.comments || '',
          saving: false,
        }))
      } catch (error) {
        this.error = 'No se pudieron cargar los reportes.'
      } finally {
        this.loading = false
      }
    },
    async saveComments(report) {
      if (!this.canComment) return
      const apiDirectory = process.env.API_DIRECTORY
      report.saving = true
      this.error = ''
      try {
        const { data } = await axios.patch(
          `${apiDirectory}/unit-malfunction-reports/${report.id}/comments/`,
          { comments: report.commentsDraft || '' },
          { headers: { Authorization: 'Token ' + localStorage.getItem('authToken') } }
        )
        report.comments = data.comments || ''
      } catch (error) {
        this.error = 'No se pudo guardar el comentario.'
      } finally {
        report.saving = false
      }
    },
    openScheduleModal(report) {
      const descriptionSnippet = String(report?.description || '').trim().slice(0, 350)
      this.selectedReportForSchedule = report
      this.scheduleForm = {
        unit_short_id: report?.unit_short_id || '',
        motive_of_service: 'Correctivo',
        scheduled_for: '',
        service_description: `ORDEN CREADA AUTOMATICAMENTE DESDE REPORTE DE FALLA #${report?.id || ''}${descriptionSnippet ? ` - ${descriptionSnippet}` : ''}`,
      }
      this.showScheduleModal = true
    },
    closeScheduleModal() {
      this.showScheduleModal = false
      this.selectedReportForSchedule = null
    },
    toServerDateTime(localValue) {
      if (!localValue) return null
      return new Date(localValue).toISOString()
    },
    async submitScheduledOrder() {
      if (!this.canScheduleMaintenance) return
      if (!this.scheduleForm.unit_short_id) {
        this.error = 'No se puede crear orden sin unidad.'
        return
      }
      if (!this.scheduleForm.scheduled_for) {
        this.error = 'Selecciona una fecha para Programada para.'
        return
      }
      if (!this.scheduleForm.motive_of_service?.trim() || !this.scheduleForm.service_description?.trim()) {
        this.error = 'Completa motivo y descripcion.'
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.creatingOrder = true
      this.error = ''
      try {
        await axios.post(
          `${apiDirectory}/create-maintenance-order`,
          {
            unit_short_id: this.scheduleForm.unit_short_id,
            motive_of_service: this.scheduleForm.motive_of_service.trim(),
            scheduled_for: this.toServerDateTime(this.scheduleForm.scheduled_for),
            service_description: this.scheduleForm.service_description.trim(),
          },
          {
            headers: { Authorization: 'Token ' + localStorage.getItem('authToken') },
          }
        )
        const unitForMessage = this.scheduleForm.unit_short_id
        this.closeScheduleModal()
        await Swal.fire({
          icon: 'success',
          title: 'Orden creada',
          text: `Se programo una orden correctiva para unidad ${unitForMessage}.`,
          confirmButtonText: 'OK',
        })
      } catch (error) {
        this.error = 'No se pudo crear la orden de mantenimiento.'
      } finally {
        this.creatingOrder = false
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('es-MX')
    },
    goBack() {
      this.$router.push({ path: '/maintenance/malfunction-reports' })
    },
  },
}
</script>

<style scoped>
.main-panel { min-height: 100vh; background: #f3f6fd; padding: 12vh 24px 48px; }
.nav-bar-operations { position: absolute; top: 0; left: 0; width: 100vw; height: 10vh; background: #ffc434; display: flex; align-items: center; padding-left: 16px; }
.logo-button { height: 55%; object-fit: contain; cursor: pointer; }
.header { margin-top: 10px; margin-bottom: 12px; display: flex; justify-content: space-between; gap: 12px; align-items: center; background: rgba(255, 255, 255, 0.78); border: 1px solid rgba(15, 23, 42, 0.08); border-radius: 16px; padding: 12px 16px; }
.header h1 { margin: 0; font-size: 1.2rem; color: #0f172a; }
.sub { margin: 3px 0 0; color: #667085; }
.header-actions { display: flex; gap: 8px; }
.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 16px; padding: 16px; max-width: 1100px; margin: 0 auto; box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08); }
.loading, .empty { color: #475467; }
.report-list { display: grid; gap: 12px; }
.report-item { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; }
.top-row { display: flex; justify-content: space-between; gap: 10px; }
.meta { margin: 6px 0; color: #334155; }
.description { margin: 10px 0; white-space: pre-wrap; color: #111827; }
.field { display: grid; gap: 6px; }
.field span { font-size: .95rem; font-weight: 600; color: #344054; }
.field textarea { border: 1px solid #d0d7de; border-radius: 10px; padding: 8px 10px; font: inherit; }
.actions { margin-top: 8px; display: flex; justify-content: flex-end; gap: 8px; }
.btn { border-radius: 10px; padding: 9px 12px; border: 1px solid transparent; cursor: pointer; }
.btn--ghost { background: #fff; border-color: #d0d7de; }
.btn--primary { background: #2563eb; color: #fff; }
.error { color: #b42318; margin-bottom: 10px; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal-card {
  width: min(560px, 100%);
  max-height: 80vh;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-head {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-head h3 { margin: 0; }

.close-btn,
.small-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
}

.small-btn.primary {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.modal-body {
  padding: 12px;
  display: grid;
  gap: 10px;
}

.form-row {
  display: grid;
  gap: 6px;
}

.form-row label {
  color: #334155;
  font-size: 0.88rem;
  font-weight: 600;
}

.form-row input,
.form-row textarea {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;
}

.form-row textarea {
  min-height: 90px;
  resize: vertical;
}

.modal-actions {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #e2e8f0;
}

.footer-actions {
  justify-content: flex-end;
}
</style>
